/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useSnackbar } from "notistack";
import { useDrive, downloadFile } from "./useDrive";
import * as drive from "../../../api/drive";
import * as favoritesApi from "../../../api/favorites";
import * as teamsApi from "../../../api/teams";
import DriveTable from "./components/DriveTable";
import UploadQueue from "./components/UploadQueue";
import FilePreviewDialog from "./components/FilePreviewDialog";
import VersionHistoryDialog from "./components/VersionHistoryDialog";
import CommentsDialog from "./components/CommentsDialog";
import LabelsDialog from "./components/LabelsDialog";
import {
  ConfirmDeleteDialog,
  MoveDialog,
  NamePromptDialog,
  ShareDialog,
} from "./components/DriveDialogs";

const DrivePage = () => {
  // `teamId` is only present on /teams/:teamId/folders/:folderId — its
  // presence is what tells this page (and, via the URL, the sidebar) that the
  // folder being browsed belongs to a team rather than to "My Drive".
  const { folderId = null, teamId = null } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { folders, files, trail, loading, error, reload, createFolder, renameFolder, renameFile } =
    useDrive(folderId);

  const [uploads, setUploads] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [dialog, setDialog] = useState(null); // {kind, item} | {kind, items}
  const [selected, setSelected] = useState(new Map()); // id -> row
  const [favorites, setFavorites] = useState(new Set());
  const [teamName, setTeamName] = useState(null);

  useEffect(() => {
    if (!teamId) {
      setTeamName(null);
      return;
    }
    teamsApi.getTeam(teamId).then((t) => setTeamName(t.teamName)).catch(() => setTeamName(null));
  }, [teamId]);

  // One request per folder view rather than one per row.
  useEffect(() => {
    favoritesApi.listFavoriteIds().then(setFavorites).catch(() => setFavorites(new Set()));
  }, [folderId]);

  // Optimistic: the star should feel instant, and a failure just reverts.
  const toggleFavorite = async (row) => {
    const wasFavorited = favorites.has(row.id);
    setFavorites((prev) => {
      const next = new Set(prev);
      if (wasFavorited) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
    try {
      await favoritesApi.setFavorite({
        resourceType: row.type,
        resourceId: row.id,
        favorited: !wasFavorited,
      });
    } catch (err) {
      setFavorites((prev) => {
        const next = new Set(prev);
        if (wasFavorited) next.add(row.id);
        else next.delete(row.id);
        return next;
      });
      enqueueSnackbar(err.message || "Could not update favorites.", { variant: "error" });
    }
  };

  // Selection is per-folder: ids from the folder you just left are meaningless
  // here, and keeping them would make the bulk bar act on invisible rows.
  useEffect(() => {
    setSelected(new Map());
  }, [folderId]);

  const toggleSelect = (row) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });

  const toggleSelectAll = (rows) =>
    setSelected((prev) =>
      rows.every((r) => prev.has(r.id)) ? new Map() : new Map(rows.map((r) => [r.id, r]))
    );

  const selectedRows = [...selected.values()];
  const clearSelection = () => setSelected(new Map());

  /** Runs an action over the selection, reporting how many failed. */
  const bulk = async (fn) => {
    const results = await Promise.allSettled(selectedRows.map(fn));
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed) {
      enqueueSnackbar(`${failed} of ${results.length} items could not be updated.`, {
        variant: "error",
      });
    }
    clearSelection();
    reload();
  };

  const moveItems = (items) => (targetFolderId) =>
    Promise.all(
      items.map((item) =>
        item.type === "folder"
          ? drive.moveFolder(item.id, targetFolderId)
          : drive.moveFile(item.id, targetFolderId)
      )
    ).then(() => {
      clearSelection();
      reload();
    });

  const deleteItems = (items) => () =>
    Promise.all(
      items.map((item) =>
        item.type === "folder" ? drive.deleteFolder(item.id) : drive.deleteFile(item.id)
      )
    ).then(() => {
      clearSelection();
      reload();
    });
  const fileInputRef = useRef(null);
  const controllersRef = useRef(new Map());
  const uploadSeq = useRef(0);

  const startUpload = useCallback(
    async (fileList) => {
      const chosen = Array.from(fileList);
      if (chosen.length === 0) return;

      // Captured now: if the user navigates away mid-upload the file must still
      // land in the folder they dropped it into, not wherever they end up.
      const targetFolderId = folderId;

      await Promise.all(
        chosen.map(async (file) => {
          const id = `u${(uploadSeq.current += 1)}`;
          const controller = new AbortController();
          controllersRef.current.set(id, controller);

          setUploads((prev) => [
            ...prev,
            { id, name: file.name, size: file.size, progress: 0, status: "uploading" },
          ]);

          const patch = (fields) =>
            setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...fields } : u)));

          try {
            await drive.uploadFile(file, {
              folderId: targetFolderId,
              signal: controller.signal,
              onProgress: (progress) => patch({ progress }),
            });
            patch({ status: "done", progress: 100 });
          } catch (err) {
            if (err.name === "CanceledError" || err.name === "AbortError") {
              setUploads((prev) => prev.filter((u) => u.id !== id));
            } else {
              patch({ status: "error", error: err.message || "Upload failed" });
            }
          } finally {
            controllersRef.current.delete(id);
          }
        })
      );

      reload();
    },
    [folderId, reload]
  );

  const cancelUpload = (id) => {
    controllersRef.current.get(id)?.abort();
  };

  const dismissFinished = () =>
    setUploads((prev) => prev.filter((u) => u.status === "uploading"));

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files?.length) startUpload(event.dataTransfer.files);
  };

  const handleDownload = async (item) => {
    try {
      await downloadFile(item.id, item.name);
    } catch (err) {
      enqueueSnackbar(err.message || "Download failed.", { variant: "error" });
    }
  };

  const openFolder = (id) =>
    navigate(teamId ? `/teams/${teamId}/folders/${id}` : `/drive/folders/${id}`);
  const closeDialog = () => setDialog(null);

  return (
    <Box
      sx={{ p: 3, height: "100%", overflow: "auto", position: "relative" }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, gap: 2 }}>
        <Breadcrumbs>
          <Link
            component="button"
            underline="hover"
            color={trail.length === 0 ? "text.primary" : "inherit"}
            onClick={() => navigate(teamId ? `/teams/${teamId}` : "/drive")}
          >
            {teamId ? teamName || "Team folder" : "My Drive"}
          </Link>
          {trail.map((crumb, index) => {
            const isLast = index === trail.length - 1;
            return isLast ? (
              <Box component="span" key={crumb.id} sx={{ color: "text.primary" }}>{crumb.name}</Box>
            ) : (
              <Link
                component="button"
                underline="hover"
                color="inherit"
                key={crumb.id}
                onClick={() => openFolder(crumb.id)}
              >
                {crumb.name}
              </Link>
            );
          })}
        </Breadcrumbs>

        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button
            startIcon={<CreateNewFolderIcon />}
            sx={{ color: "text.color", textTransform: "none" }}
            onClick={() => setDialog({ kind: "create-folder" })}
          >
            New folder
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            sx={{ backgroundColor: "accent.solid", color: "#fff", textTransform: "none" }}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              startUpload(e.target.files);
              e.target.value = ""; // allow re-picking the same file
            }}
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={reload}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {selectedRows.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
            px: 2,
            py: 1,
            borderRadius: "10px",
            backgroundColor: "tertiary.main",
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 500, flex: 1 }}>
            {selectedRows.length} selected
          </Typography>
          <Button
            size="small"
            startIcon={<DriveFileMoveOutlinedIcon />}
            sx={{ color: "text.color", textTransform: "none" }}
            onClick={() => setDialog({ kind: "move", items: selectedRows })}
          >
            Move
          </Button>
          <Button
            size="small"
            startIcon={<DownloadOutlinedIcon />}
            sx={{ color: "text.color", textTransform: "none" }}
            disabled={!selectedRows.some((r) => r.type === "file")}
            onClick={() => bulk((row) => (row.type === "file" ? downloadFile(row.id, row.name) : null))}
          >
            Download
          </Button>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            sx={{ color: "error.main", textTransform: "none" }}
            onClick={() => setDialog({ kind: "delete", items: selectedRows })}
          >
            Delete
          </Button>
          <Button size="small" sx={{ color: "text.color", textTransform: "none" }} onClick={clearSelection}>
            Clear
          </Button>
        </Box>
      )}

      <Paper
        variant="outlined"
        sx={{
          minHeight: 240,
          outline: dragging ? "2px dashed" : "none",
          outlineColor: "accent.main",
          outlineOffset: -8,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "accent.main" }} />
          </Box>
        ) : (
          <DriveTable
            folders={folders}
            files={files}
            selected={new Set(selected.keys())}
            favorites={favorites}
            onToggleFavorite={toggleFavorite}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
            onOpenFolder={openFolder}
            onPreview={(item) => setDialog({ kind: "preview", item })}
            onDownload={handleDownload}
            onVersionHistory={(item) => setDialog({ kind: "versions", item })}
            onComments={(item) => setDialog({ kind: "comments", item })}
            onLabels={(item) => setDialog({ kind: "labels", item })}
            onRename={(item) => setDialog({ kind: "rename", item })}
            onMove={(item) => setDialog({ kind: "move", items: [item] })}
            onShare={(item) => setDialog({ kind: "share", item })}
            onDelete={(item) => setDialog({ kind: "delete", items: [item] })}
          />
        )}
      </Paper>

      <UploadQueue uploads={uploads} onCancel={cancelUpload} onDismiss={dismissFinished} />

      <NamePromptDialog
        open={dialog?.kind === "create-folder"}
        title="New folder"
        label="Folder name"
        confirmLabel="Create"
        onConfirm={createFolder}
        onClose={closeDialog}
      />
      <MoveDialog
        open={dialog?.kind === "move"}
        items={dialog?.items || []}
        onConfirm={moveItems(dialog?.items || [])}
        onClose={closeDialog}
      />
      <NamePromptDialog
        open={dialog?.kind === "rename"}
        title={`Rename ${dialog?.item?.type ?? ""}`}
        label="Name"
        initialValue={dialog?.item?.name ?? ""}
        confirmLabel="Rename"
        onConfirm={(name) =>
          dialog.item.type === "folder"
            ? renameFolder(dialog.item.id, name)
            : renameFile(dialog.item.id, name)
        }
        onClose={closeDialog}
      />
      <ConfirmDeleteDialog
        open={dialog?.kind === "delete"}
        items={dialog?.items || []}
        onConfirm={deleteItems(dialog?.items || [])}
        onClose={closeDialog}
      />
      <ShareDialog open={dialog?.kind === "share"} item={dialog?.item} onClose={closeDialog} />
      <FilePreviewDialog open={dialog?.kind === "preview"} file={dialog?.item} onClose={closeDialog} />
      <VersionHistoryDialog open={dialog?.kind === "versions"} file={dialog?.item} onClose={closeDialog} />
      <CommentsDialog open={dialog?.kind === "comments"} file={dialog?.item} onClose={closeDialog} />
      <LabelsDialog open={dialog?.kind === "labels"} file={dialog?.item} onClose={closeDialog} />
    </Box>
  );
};

export default DrivePage;
